import { useEffect } from 'react';
import { useLocation } from 'react-router';

// Screenshot/copy/DevTools lockdown protects pages that expose proprietary
// business data (formulations, costing, etc). Arey is an internal chat tool
// — copy/paste and normal text selection are expected there, and a message
// already has an explicit "Copy text" action that bypasses this anyway, so
// the lockdown adds friction without adding real protection on that page.
const EXEMPT_PATH_PREFIXES = ['/arey'];

export default function useScreenshotPrevention() {
  const location = useLocation();
  const exempt = EXEMPT_PATH_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (exempt) return;
    // ── Context menu prevention (all platforms) ──
    const preventContext = (e) => e.preventDefault();
    document.addEventListener('contextmenu', preventContext);

    // ── CSS protections ──
    const style = document.createElement('style');
    style.id = 'screenshot-prevention';
    style.textContent = `
      body {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        -webkit-touch-callout: none !important;
      }
      input, textarea, [contenteditable], [data-selectable], [data-selectable] * {
        -webkit-user-select: text !important;
        user-select: text !important;
      }
      /* Print is blocked by default (anti-leak). The app's own Print buttons
         opt in by setting body[data-aone-print-target] before calling
         window.print() (see triggerAonePrint in PurchaseOrders.js), which
         reveals only the intended printable region (marked with an
         .aone-*-print wrapper class) instead of the whole app. */
      @media print {
        body { display: none !important; }
        body[data-aone-print-target] { display: block !important; }
        body[data-aone-print-target] * { visibility: hidden !important; }

        body[data-aone-print-target="list"] .aone-po-list-print,
        body[data-aone-print-target="list"] .aone-po-list-print *,
        body[data-aone-print-target="panel"] .aone-po-panel-print,
        body[data-aone-print-target="panel"] .aone-po-panel-print * {
          visibility: visible !important;
        }
        body[data-aone-print-target] .aone-po-list-print button,
        body[data-aone-print-target] .aone-po-panel-print button {
          display: none !important;
        }

        /* The app shell clips overflow to the viewport height — release it
           so multi-page content isn't cut off instead of just going blank.
           (Keep their own display: flex — switching to block would stack
           the sidebar above the content pane instead of beside it, pushing
           everything down by the sidebar's full — hidden but still
           space-reserving — height.) */
        body[data-aone-print-target] .aone-root-wrap,
        body[data-aone-print-target] .aone-main-area,
        body[data-aone-print-target] #aone-content {
          overflow: visible !important;
          height: auto !important;
        }
        /* Sidebar nav is always the root wrap's first child on desktop —
           drop it entirely rather than just hiding it, so it doesn't still
           reserve its (long) height next to the printable content. */
        body[data-aone-print-target] .aone-root-wrap > div:first-child {
          display: none !important;
        }
        /* visibility:hidden (unlike display:none) keeps hidden siblings'
           layout space reserved — without pulling the printable target out
           of flow, it renders thousands of px below the hidden content
           above it and looks blank. position:absolute escapes that. */
        body[data-aone-print-target="list"] .aone-po-list-print,
        body[data-aone-print-target="panel"] .aone-po-panel-print {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
        }
        /* The detail panel is itself a fixed, viewport-height overlay —
           flatten its internals into normal flow so it paginates instead of
           clipping to one screen. */
        body[data-aone-print-target="panel"] .aone-po-panel-print div {
          position: static !important;
          inset: auto !important;
          overflow: visible !important;
          height: auto !important;
          max-height: none !important;
          width: 100% !important;
          max-width: 100% !important;
          background: none !important;
          box-shadow: none !important;
        }
      }
    `;
    document.head.appendChild(style);

    // ── Visibility blur — blur when tab not active (screenshot tools often hide tab) ──
    const handleVisibilityChange = () => {
      if (document.hidden) {
        document.body.style.filter = 'blur(20px)';
      } else {
        document.body.style.filter = 'none';
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // ── Keyboard shortcut prevention (desktop) ──
    const preventKeys = (e) => {
      // PrintScreen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        document.body.style.filter = 'blur(20px)';
        setTimeout(() => { document.body.style.filter = 'none'; }, 2000);
      }
      // Ctrl+Shift+S (save as)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
      }
      // Ctrl+P (print)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
      }
      // Ctrl+Shift+I / Cmd+Option+I (DevTools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'i' || e.key === 'I')) {
        e.preventDefault();
      }
      // F12 (DevTools)
      if (e.key === 'F12') {
        e.preventDefault();
      }
      // Ctrl+U (View Source)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'u' || e.key === 'U')) {
        e.preventDefault();
      }
      // Cmd+Shift+3/4/5 (macOS screenshots) — can't fully prevent but blur on keydown
      if (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key)) {
        document.body.style.filter = 'blur(20px)';
        setTimeout(() => { document.body.style.filter = 'none'; }, 2000);
      }
    };
    document.addEventListener('keydown', preventKeys);

    // ── DevTools detection (desktop) ──
    // Monitors window resize that doesn't match screen — possible DevTools dock
    let devToolsOpen = false;
    const devToolsCheck = setInterval(() => {
      const widthThreshold = window.outerWidth - window.innerWidth > 200;
      const heightThreshold = window.outerHeight - window.innerHeight > 200;
      if (widthThreshold || heightThreshold) {
        if (!devToolsOpen) {
          devToolsOpen = true;
          console.warn('[Security] Developer tools detected — formulation data is monitored.');
        }
      } else {
        devToolsOpen = false;
      }
    }, 2000);

    return () => {
      document.removeEventListener('contextmenu', preventContext);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('keydown', preventKeys);
      clearInterval(devToolsCheck);
      const existingStyle = document.getElementById('screenshot-prevention');
      if (existingStyle) existingStyle.remove();
      document.body.style.filter = 'none';
    };
  }, [exempt]);
}
